// X78 (R27 N3): THE way a membership type's name and its noun are joined on
// the web, the twin of the API's MembershipWording.WithNoun (and the app's
// memberships_words.typeThing). The noun comes once: "Family membership",
// "Mixer pass"; a type whose name already ends in the word ("Tennis
// membership", "Season pass") keeps its name alone, so nothing ever reads
// "Tennis membership membership". Every "{name} membership" sentence goes
// through here; tests/unit/booking-r27-fixes.test.ts greps the app for any
// that does not.

/** "membership" / "pass" as a noun in a sentence (the API's DisplayAs). */
export function nounFor(displayAs) {
  return displayAs === 'Pass' ? 'pass' : 'membership';
}

/** Whether the name itself ends in the noun as a whole word ("Tennis membership" does, "Compass" does not end in "pass"). */
export function nameSaysNoun(name, noun) {
  const n = String(name ?? '').trim();
  const w = String(noun ?? '').trim();
  if (!n || !w) return false;
  if (n.length === w.length) return n.toLowerCase() === w.toLowerCase();
  return n.toLowerCase().endsWith(w.toLowerCase()) && /\s/.test(n[n.length - w.length - 1]);
}

/**
 * "Senior membership" / "Mixer pass" / "Tennis membership": the type's name with its noun once.
 * `displayAs` is the API's DisplayAs ('Membership' | 'Pass') or the bare noun ('membership' | 'pass').
 */
export function withNoun(name, displayAs = 'Membership') {
  const noun = displayAs === 'membership' || displayAs === 'pass' ? displayAs : nounFor(displayAs);
  const n = String(name ?? '').trim();
  if (!n) return noun;
  return nameSaysNoun(n, noun) ? n : `${n} ${noun}`;
}

/** The plural: "Parent Play memberships" / "Tennis memberships" / "Mixer passes". */
export function withNouns(name, displayAs = 'Membership') {
  const noun = displayAs === 'membership' || displayAs === 'pass' ? displayAs : nounFor(displayAs);
  return withNoun(name, noun) + (noun.endsWith('s') ? 'es' : 's');
}

// ── A term's money, said once (X82, X81's leftover) ──────────────────────
//
// The admin's View membership read "Refunded 20/Oct/26 · £240.00 refunded"
// and the member's dialog "£240.00 · refunded · £240.00 refunded" on a term
// refunded in full: the status already says it. A full refund reads the
// status "Refunded" with ONE amount; a part refund keeps the amount and the
// status with the refund after them ("£240.00 · paid · £2.50 refunded").

/** What a term cost: the price plus any joining fee (the admin's periodAmountMinor adds the offline amount itself). */
export function periodTotalMinor(p) {
  return (p?.amountMinor ?? 0) + (p?.joiningFeeMinor ?? 0);
}

/** Whether the term's money all went back: the API's Refunded status, or a refund that covers the whole amount. */
export function periodRefundedInFull(p, totalMinor = periodTotalMinor(p)) {
  const refunded = p?.refundedMinor ?? 0;
  if (p?.status === 'Refunded') return true;
  return refunded > 0 && totalMinor > 0 && refunded >= totalMinor;
}

/**
 * The refund words after a term's status: "£2.50 refunded" after a part refund; null with no refund, and null when the
 * term is refunded in full (the status "Refunded" says it and the amount is already on the line).
 */
export function periodRefundWords(p, money, totalMinor = periodTotalMinor(p)) {
  const refunded = p?.refundedMinor ?? 0;
  if (refunded <= 0 || periodRefundedInFull(p, totalMinor)) return null;
  return `${money(refunded, p.currency)} refunded`;
}

/**
 * The member's one-line money of a term: "£240.00 · paid", "£240.00 · paid · £2.50 refunded", "Refunded £240.00" (in full),
 * "Free · paid". `statusWord` is the period's status in words ("paid", "due", "declined"…).
 */
export function periodMoneyLine(p, {money, statusWord, free = 'Free'}) {
  const total = periodTotalMinor(p);
  if (periodRefundedInFull(p, total)) return `Refunded ${money(p.refundedMinor > 0 ? p.refundedMinor : total, p.currency)}`;
  const amount = total > 0 ? money(total, p.currency) : free;
  const refund = periodRefundWords(p, money, total);
  return `${amount} · ${statusWord}${refund ? ` · ${refund}` : ''}`;
}
